// SSE (Server-Sent Events) client for real-time updates

export interface SSEOptions {
  onMessage?: (event: MessageEvent) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
  reconnect?: boolean;
  reconnectInterval?: number;
}

export class SSEClient {
  private eventSource: EventSource | null = null;
  private url: string;
  private options: SSEOptions;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private isManualClose = false;

  constructor(url: string, options: SSEOptions = {}) {
    this.url = url;
    this.options = {
      reconnect: true,
      reconnectInterval: 3000,
      ...options,
    };
  }

  connect(): void {
    if (this.eventSource) {
      return; // Already connected
    }

    this.isManualClose = false;

    try {
      this.eventSource = new EventSource(this.url);

      this.eventSource.onopen = () => {
        console.log('SSE connected:', this.url);
        if (this.options.onOpen) {
          this.options.onOpen();
        }
        // Clear any pending reconnect
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
        }
      };

      this.eventSource.onmessage = (event) => {
        if (this.options.onMessage) {
          this.options.onMessage(event);
        }
      };

      this.eventSource.onerror = (error) => {
        console.error('SSE error:', error);
        if (this.options.onError) {
          this.options.onError(error);
        }

        // Close the connection
        this.eventSource?.close();
        this.eventSource = null;

        // Attempt reconnect if enabled and not manually closed
        if (this.options.reconnect && !this.isManualClose) {
          console.log(
            `Reconnecting in ${this.options.reconnectInterval}ms...`
          );
          this.reconnectTimeout = setTimeout(() => {
            this.connect();
          }, this.options.reconnectInterval);
        }
      };

      // Custom event listeners
      this.addEventListener('connected', (event) => {
        console.log('SSE connection confirmed:', event.data);
      });

      this.addEventListener('ping', () => {
        // Keep-alive ping received
      });
    } catch (error) {
      console.error('Failed to create EventSource:', error);
      if (this.options.onError) {
        this.options.onError(error as Event);
      }
    }
  }

  addEventListener(
    eventName: string,
    listener: (event: MessageEvent) => void
  ): void {
    if (!this.eventSource) {
      console.warn('Cannot add event listener: not connected');
      return;
    }

    this.eventSource.addEventListener(eventName, listener as EventListener);
  }

  removeEventListener(
    eventName: string,
    listener: (event: MessageEvent) => void
  ): void {
    if (!this.eventSource) {
      return;
    }

    this.eventSource.removeEventListener(eventName, listener as EventListener);
  }

  disconnect(): void {
    this.isManualClose = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      console.log('SSE disconnected:', this.url);
    }
  }

  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  getReadyState(): number {
    return this.eventSource?.readyState ?? EventSource.CLOSED;
  }
}

// KDS-specific SSE client
export class KDSSSEClient extends SSEClient {
  constructor(branchId: string, options: Omit<SSEOptions, 'onMessage'> = {}) {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const url = `${apiUrl}/sse/kds/${branchId}`;

    super(url, options);
  }

  onKDSUpdate(callback: (data: any) => void): void {
    this.addEventListener('kds-update', (event) => {
      try {
        const data = JSON.parse(event.data);
        callback(data);
      } catch (error) {
        console.error('Failed to parse KDS update:', error);
      }
    });
  }
}

// Orders-specific SSE client
export class OrdersSSEClient extends SSEClient {
  constructor(branchId: string, options: Omit<SSEOptions, 'onMessage'> = {}) {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const url = `${apiUrl}/sse/orders/${branchId}`;

    super(url, options);
  }

  onOrderEvent(callback: (event: any) => void): void {
    this.addEventListener('order-event', (event) => {
      try {
        const data = JSON.parse(event.data);
        callback(data);
      } catch (error) {
        console.error('Failed to parse order event:', error);
      }
    });
  }
}
